import { useEffect, useState, useCallback } from "react";
import { Radio, Heart, Bell, Music, ArrowRight } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

export const ListenerOverview = ({ currentUser, authToken, navigate }) => {
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => {
    const h = { "Content-Type": "application/json" };
    if (authToken) h["Authorization"] = `Bearer ${authToken}`;
    return h;
  }, [authToken]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/follows/my-follows`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setFollowing(Array.isArray(data) ? data : []))
      .catch(() => setFollowing([]))
      .finally(() => setLoading(false));
  }, [headers]);

  return (
    <div className="space-y-6" data-testid="listener-overview">
      {/* Welcome */}
      <div className="bg-gradient-to-br from-green-500/10 to-emerald-600/5 border border-green-500/20 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-1">
          Welcome back, {currentUser?.name?.split(" ")[0] || "Listener"}
        </h2>
        <p className="text-white/40 text-sm">
          Discover stations, follow your favorites, and get notified when they go live.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Heart className="w-5 h-5 text-pink-400" />
          </div>
          <div className="text-2xl font-bold text-white">{following.length}</div>
          <div className="text-white/40 text-xs">Stations Following</div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-5 h-5 text-amber-400" />
          </div>
          <div className="text-2xl font-bold text-white">
            {following.filter(f => f.notify).length}
          </div>
          <div className="text-white/40 text-xs">Notifications On</div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-5 col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-2">
            <Radio className="w-5 h-5 text-green-400" />
          </div>
          <div className="text-2xl font-bold text-white">Free</div>
          <div className="text-white/40 text-xs">Your Plan</div>
        </div>
      </div>

      {/* Following List */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-white">Stations You Follow</h3>
        </div>
        {loading ? (
          <div className="text-white/30 text-sm py-8 text-center">Loading...</div>
        ) : following.length === 0 ? (
          <div className="text-center py-8">
            <Music className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-white/40 text-sm font-medium">No stations followed yet</p>
            <p className="text-white/25 text-xs mt-1 mb-4">
              Head to Discover to find stations you love.
            </p>
            <button onClick={() => navigate("/discover")}
              className="inline-flex items-center gap-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/20 text-green-400 px-5 py-2.5 rounded-xl text-xs font-medium transition"
              data-testid="listener-discover-cta">
              <Radio className="w-3.5 h-3.5" /> Discover Stations
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {following.map((station, i) => (
              <div key={i} className="flex items-center justify-between bg-white/[0.03] rounded-xl px-4 py-3 hover:bg-white/[0.06] transition"
                data-testid={`followed-station-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Radio className="w-4 h-4 text-purple-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{station.station_name || station.station_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {station.notify && (
                    <span className="text-amber-400/60 text-[10px] flex items-center gap-1">
                      <Bell className="w-3 h-3" /> Notify
                    </span>
                  )}
                  <button onClick={() => navigate(`/player/${station.station_id}`)}
                    className="text-purple-400/60 hover:text-purple-400 transition p-1">
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Discover CTA */}
      <div className="bg-gradient-to-r from-purple-500/5 to-indigo-500/5 border border-purple-500/15 rounded-2xl p-6 text-center">
        <p className="text-white/50 text-sm font-medium mb-3">Find more stations to listen to</p>
        <button onClick={() => navigate("/discover")}
          className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-medium text-sm transition"
          data-testid="listener-discover-btn">
          <Radio className="w-4 h-4" /> Browse All Stations <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
